'use client';

import { useState, useEffect } from 'react';
import axios from 'axios';
import { useTranslation } from '@/context/LanguageContext';
import type { ProgramStudiResponse } from '@/types/prodi';
import ProdiSection from './ProdiSection';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { Bar, Doughnut } from 'react-chartjs-2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faUniversity,
  faChalkboardTeacher,
  faUserGraduate,
  faGraduationCap,
  faIdBadge,
  faChartPie,
  faChartBar,
  faInfoCircle,
  faCalendarAlt,
  IconDefinition
} from '@fortawesome/free-solid-svg-icons';
import { siteConfig } from '@/config/site';

// Register Chart.js components
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);



// Icon mapping for dynamic lookup
const iconMap: Record<string, IconDefinition> = {
  'fa-users': faUsers,
  'fa-university': faUniversity,
  'fa-chalkboard-teacher': faChalkboardTeacher,
  'fa-user-graduate': faUserGraduate,
  'fa-graduation-cap': faGraduationCap,
  'fa-id-badge': faIdBadge,
};

// Updated stats data from UNMUL 2025 data
const statsData = [
  {
    labelId: 'Mahasiswa Aktif',
    labelEn: 'Active Students',
    value: '32.254',
    numValue: 32254,
    subtitle: '2025/2026 Ganjil',
    icon: 'fa-users',
    color: 'from-unmulGreen to-emerald-600',
    bgColor: 'bg-green-100',
  },
  {
    labelId: 'Fakultas',
    labelEn: 'Faculties',
    value: '14',
    numValue: 14,
    icon: 'fa-university',
    color: 'from-unmulYellow to-amber-500',
    bgColor: 'bg-amber-100',
  },
  {
    labelId: 'Dosen',
    labelEn: 'Lecturers',
    value: '1.078',
    numValue: 1078,
    subtitle: 'S2, S3, Profesor',
    icon: 'fa-chalkboard-teacher',
    color: 'from-unmulGreen to-emerald-600',
    bgColor: 'bg-green-100',
  },
  {
    labelId: 'Alumni',
    labelEn: 'Alumni',
    value: '>150.000',
    numValue: 150000,
    icon: 'fa-user-graduate',
    color: 'from-unmulYellow to-amber-500',
    bgColor: 'bg-amber-100',
  },
  {
    labelId: 'Program Studi',
    labelEn: 'Study Programs',
    value: '103',
    numValue: 103,
    icon: 'fa-graduation-cap',
    color: 'from-unmulGreen to-emerald-600',
    bgColor: 'bg-green-100',
  },
  {
    labelId: 'Tenaga Kependidikan',
    labelEn: 'Education Staff',
    value: '1.150',
    numValue: 1150,
    icon: 'fa-id-badge',
    color: 'from-unmulYellow to-amber-500',
    bgColor: 'bg-amber-100',
  },
];



// Staff distribution for bar visualization
// Staff distribution for bar visualization
const staffData = [
  { name: 'Profesor', value: 85, color: '#006633' },
  { name: 'Lektor Kepala', value: 280, color: '#FFCC00' },
  { name: 'Lektor', value: 350, color: '#10B981' },
  { name: 'Asisten Ahli', value: 363, color: '#60A5FA' },
];

export default function StatsSection() {
  const { language, t } = useTranslation();
  const [activeStudentCount, setActiveStudentCount] = useState<string>('0');
  const [activeStudentSemester, setActiveStudentSemester] = useState<string>('2025/2026 Ganjil');
  const [studentByYear, setStudentByYear] = useState<any[]>([]);
  const [fakultasCount, setFakultasCount] = useState<string>('0');
  const [programStudiCount, setProgramStudiCount] = useState<string>('0');
  const [prodiList, setProdiList] = useState<ProgramStudiResponse | null>(null);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    setIsMounted(true);
  }, []);


  // Determine subtitle based on webType
  const webType = siteConfig.webType; // Correctly placed before usage
  
  // State for stats
  const [kepegawaianStats, setKepegawaianStats] = useState<any>(null);

  useEffect(() => {
    // Helper to calculate current semester code (e.g., 20251 or 20252)
    const calculateSemester = (month: number, year: number) => {
      if (month < 6) {
        return `${year - 1}2`;
      } else {
        return `${year}1`;
      }
    };

    const fetchData = async () => {
      try {
        const now = new Date();
        const month = now.getMonth();
        const year = now.getFullYear();

        let angkatanAkhir = year;
        let semesterStr = '';

        if (month < 6) {
          // Jan - Jun
          angkatanAkhir = year - 1; // e.g. 2026 -> 2025
          semesterStr = `${angkatanAkhir}/${year} Genap`;
        } else {
          // Jul - Dec
          angkatanAkhir = year;
          semesterStr = `${angkatanAkhir}/${year + 1} Ganjil`;
        }

        const angkatanAwal = 2018; // Fixed as per request
        setActiveStudentSemester(semesterStr);

        // Fetch Unit Info first
        const unitInfoResponse = await axios.get('/api/unit-info');
        const unitInfo = unitInfoResponse.data;

        // --- 1. Fetch Student Stats ---
        let studentEndpoint = `sia/v1/laporan/keadaan_mahasiswa?tipe=universitas&angkatan_awal=${angkatanAwal}&angkatan_akhir=${angkatanAkhir}`;

        if (unitInfo) {
          const currentSemesterCode = calculateSemester(month, year);
          
          if (unitInfo.program_studi) {
             studentEndpoint = `sia/v1/laporan/keadaan_mahasiswa?tipe=prodi&angkatan_awal=${angkatanAwal}&angkatan_akhir=${year}&prodi=${unitInfo.program_studi.prodikode}&semester=${currentSemesterCode}`;
          } else if (unitInfo.fakultas) {
             studentEndpoint = `sia/v1/laporan/keadaan_mahasiswa?tipe=fakultas&angkatan_awal=${angkatanAwal}&angkatan_akhir=${year}&fakultas=${unitInfo.fakultas.fakkode}&semester=${currentSemesterCode}`;
          }
        }

        const studentResponse = await axios.post('/api/doServices', {
          method: 'GET',
          endpoint: studentEndpoint
        });

        const studentData = studentResponse.data;
        if (studentData && studentData.card && studentData.card.total) {
          setActiveStudentCount(studentData.card.total.toLocaleString('id-ID'));
        }
        if (studentData && studentData.data) {
          const sortedData = [...studentData.data].sort((a, b) => parseInt(a.angkatan) - parseInt(b.angkatan));
          setStudentByYear(sortedData);
        }

        // --- 2. Fetch Kepegawaian Stats ---
        // Need first 2 digits of unit_kepegawaian.kode
        if (unitInfo && unitInfo.unit_kepegawaian && unitInfo.unit_kepegawaian.kode) {
            const unitKode = unitInfo.unit_kepegawaian.kode.substring(0, 2);

            const kepegawaianResponse = await axios.post('/api/doServices', {
                method: 'GET',
                endpoint: `kepegawaian/rekap/dashboard?unit_kode=${unitKode}`
            });

            const kpData = kepegawaianResponse.data;
            if (kpData && kpData.data) {
                setKepegawaianStats(kpData.data);
            }
        }

        // --- 3. Fetch Program Studi (Faculty only) ---
        if (webType === '1' && unitInfo && unitInfo.fakultas && unitInfo.fakultas.fakkode) {
            const fakKode = unitInfo.fakultas.fakkode;

            try {
                const prodiResponse = await axios.post('/api/doServices', {
                    method: 'GET',
                    endpoint: `web/program_studi?limit=100&page=1&fakultas=${fakKode}`
                });

                const prodiData = prodiResponse.data as ProgramStudiResponse;
                if (prodiData && prodiData.data) {
                    setProdiList(prodiData);
                    setProgramStudiCount(prodiData.metadata.total.toLocaleString('id-ID'));
                }
            } catch (error) {
                console.error('Error fetching program studi:', error);
            }
        }

      } catch (error) {
        console.error('Error fetching stats:', error);
      }
    };

    fetchData();

    const fetchSummaryStats = async () => {
        // Keeping summary stats for fallback or other data if needed
        // Note: Currently we are mostly relying on the specific fetches above for Students/Dosen
      try {
        const year = new Date().getFullYear();
        const response = await axios.post('/api/doServices', {
          method: 'GET',
          endpoint: `sia/summary/general?tahun=${year}&tipe=1&kode=0`
        });
        const apiData = response.data;
        if (apiData && apiData.data) {
           // Only set if we haven't set them from specific APIs, OR allows overwriting constants like 'fakultasCount'
          if (apiData.data.fakultas) {
            setFakultasCount(apiData.data.fakultas.toLocaleString('id-ID'));
          }
          if (apiData.data.program_studi) {
            setProgramStudiCount(apiData.data.program_studi.toLocaleString('id-ID'));
          }
        }
      } catch (error) {
        console.error('Error fetching summary stats:', error);
      }
    };
    fetchSummaryStats();
  }, []);


  // Prepare Dynamic Staff Data
  let dynamicStaffData = staffData;
  let tendikRankData: any[] = [];
  let dosenEducationData: any[] = [];
  let dosenSerdosData: any[] = [];
  let employmentData: any = { labels: ['PNS', 'PPPK'], datasets: [] };
  
  let dosenTotal = '0';
  let tendikTotal = '0';

  if (kepegawaianStats) {
      // Dosen Stats
      if (kepegawaianStats.dosen) {
          const d = kepegawaianStats.dosen;
          
          // Jabfung (Existing)
          const jabfung = d.jabfung_dosen;
          if (jabfung) {
              dynamicStaffData = [
                  { name: 'Profesor', value: jabfung['1_Guru Besar'] || 0, color: '#006633' },
                  { name: 'Lektor Kepala', value: jabfung['2_Lektor Kepala'] || 0, color: '#FFCC00' },
                  { name: 'Lektor', value: jabfung['3_Lektor'] || 0, color: '#10B981' },
                  { name: 'Asisten Ahli', value: jabfung['4_Asisten Ahli'] || 0, color: '#60A5FA' },
              ];
          }

          // Education (S2/S3)
          if (d.s2s3_dosen) {
              dosenTotal = d.s2s3_dosen.jumlah_dosen.toLocaleString('id-ID');
              const s3 = d.s2s3_dosen.jumlah_s3 || 0;
              const s2 = d.s2s3_dosen.jumlah_s2 || 0;
              const other = d.s2s3_dosen.jumlah_dosen - s3 - s2;
              dosenEducationData = [
                  { name: 'S3 (Doktor)', value: s3, color: '#006633' },
                  { name: 'S2 (Magister)', value: s2, color: '#FFCC00' },
                  { name: 'Lainnya', value: other > 0 ? other : 0, color: '#e5e7eb' },
              ];
          }

          // Serdos
          if (d.total_serdos) {
              const serdos = d.total_serdos.total_serdos || 0;
              const nonSerdos = d.total_serdos.total_dosen - serdos;
              dosenSerdosData = [
                  { name: 'Serdos', value: serdos, color: '#10B981' },
                  { name: 'Non-Serdos', value: nonSerdos, color: '#ef4444' },
              ];
          }
      }

      // Tendik Stats
      if (kepegawaianStats.tendik) {
         const t = kepegawaianStats.tendik;
         
         // Tendik Pangkat
         if (t.pangkat_tendik) {
             const keys = Object.keys(t.pangkat_tendik).filter(k => k !== 'jumlah_tendik');
             tendikRankData = keys.map(k => ({
                 name: k,
                 value: t.pangkat_tendik[k],
                 color: '#60A5FA' // Uniform color for now
             })).sort((a,b) => b.value - a.value).slice(0, 5); // Take top 5 for neatness? Or show all
         }

         if (t.pegawai_jenis_tendik && t.pegawai_jenis_tendik.jumlah_tendik) {
             tendikTotal = t.pegawai_jenis_tendik.jumlah_tendik.toLocaleString('id-ID');
         }
      }

      // Employment Status (Dosen vs Tendik)
      if (kepegawaianStats.dosen && kepegawaianStats.dosen.pegawai_jenis_dosen && kepegawaianStats.tendik && kepegawaianStats.tendik.pegawai_jenis_tendik) {
          const dPns = kepegawaianStats.dosen.pegawai_jenis_dosen.PNS || 0;
          const dPppk = kepegawaianStats.dosen.pegawai_jenis_dosen.PPPK || 0;
          const tPns = kepegawaianStats.tendik.pegawai_jenis_tendik.PNS || 0;
          const tPppk = kepegawaianStats.tendik.pegawai_jenis_tendik.PPPK || 0;

          employmentData = {
              labels: ['PNS', 'PPPK'],
              datasets: [
                  {
                      label: 'Dosen',
                      data: [dPns, dPppk],
                      backgroundColor: '#006633',
                  },
                  {
                      label: 'Tendik',
                      data: [tPns, tPppk],
                      backgroundColor: '#FFCC00',
                  }
              ]
          };
      }
  }

  // Merge dynamic data with static data
  const displayStats = statsData.filter(stat => {
      // Remove 'Fakultas' and 'Alumni' always
      if (stat.labelId === 'Fakultas' || stat.labelId === 'Alumni') return false;
      
      // Show 'Program Studi' ONLY if webType is '1' (Fakultas)
      if (stat.labelId === 'Program Studi') return webType === '1';

      return true;
  }).map(stat => {
    if (stat.labelId === 'Mahasiswa Aktif') {
      return { ...stat, value: activeStudentCount, subtitle: activeStudentSemester };
    }
    if (stat.labelId === 'Program Studi') {
        return { ...stat, value: programStudiCount };
    }
    if (stat.labelId === 'Dosen') {
        return { ...stat, value: dosenTotal };
    }
    if (stat.labelId === 'Tenaga Kependidikan') {
        return { ...stat, value: tendikTotal };
    }
    return stat;
  });



  // Chart.js data for bar chart (students by year)
  const barChartData = {
    labels: studentByYear.map(d => d.angkatan),
    datasets: [{
      label: language === 'en' ? 'Total Students' : 'Total Mahasiswa',
      data: studentByYear.map(d => d.total),
      backgroundColor: '#006633',
      borderRadius: 4,
      barThickness: 40,
    }]
  };

  const barChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: false },
      tooltip: {
        callbacks: {
          label: (context: any) => `${context.raw.toLocaleString('id-ID')} ${language === 'en' ? 'students' : 'mahasiswa'}`
        }
      }
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#6B7280', font: { size: 12 } }
      },
      y: {
        grid: { color: '#f3f4f6', drawBorder: false },
        ticks: { color: '#6B7280', font: { size: 12 } }
      }
    }
  };

  let subtitleId = 'Perguruan tinggi negeri terbesar di Kalimantan Timur dengan komitmen pendidikan berkualitas';
  let subtitleEn = 'The largest state university in East Kalimantan with a commitment to quality education';

  if (webType === '1') { // FAKULTAS
    subtitleId = 'Fakultas terdepan dalam pengembangan ilmu pengetahuan dan teknologi';
    subtitleEn = 'Leading faculty in the development of science and technology';
  } else if (webType === '2') { // PRODI
    subtitleId = 'Program studi unggulan yang mencetak lulusan kompeten dan berdaya saing';
    subtitleEn = 'Excellent study program producing competent and competitive graduates';
  } else if (webType === '3') { // UNIT
    subtitleId = 'Unit kerja yang profesional dan berdedikasi dalam pelayanan';
    subtitleEn = 'Professional work unit dedicated to service excellence';
  }

  const siteName = siteConfig.name || "UNMUL";

  return (
    <>
    <section id="stats" className="py-24 bg-gradient-to-b from-white to-unmulPale relative overflow-hidden">
      {/* Decorative Elements */}
      <div className="absolute top-20 right-0 w-96 h-96 bg-unmulGreen/5 rounded-full blur-3xl"></div>
      <div className="absolute bottom-0 left-0 w-72 h-72 bg-unmulYellow/10 rounded-full blur-3xl"></div>

      <div className="container mx-auto px-6 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16" data-aos="fade-up">
          <span className="inline-block bg-unmulGreen text-white px-4 py-2 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
            {language === 'en' ? 'Statistics' : 'Statistik'}
          </span>
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-gray-800">
            {language === 'en' 
              ? `${process.env.NEXT_PUBLIC_SITE_NAME_ENGLISH || siteConfig.name} in Numbers` 
              : `${process.env.NEXT_PUBLIC_SITE_NAME || siteConfig.name} dalam Angka`}
          </h2>
          <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
            {language === 'en' ? subtitleEn : subtitleId}
          </p>
        </div>

        {/* Stats Grid */}
        <div className="flex flex-wrap justify-center gap-6 mb-16 max-w-5xl mx-auto">
          {displayStats.map((stat, index) => {
            // Alternating colors: green-yellow-green-yellow pattern
            const bgColor = index % 2 === 0 ? 'bg-green-50' : 'bg-amber-50';
            const iconGradient = index % 2 === 0 ? 'from-unmulGreen to-emerald-600' : 'from-unmulYellow to-amber-500';
            
            return (
            <div
              key={stat.labelId}
              className={`${bgColor} rounded-2xl p-6 text-center transform hover:-translate-y-2 transition duration-300 shadow-lg hover:shadow-xl border border-white w-full sm:w-auto sm:min-w-[200px] sm:max-w-[220px]`}
              data-aos="fade-up"
              data-aos-delay={index * 50}
            >
              <div className={`w-14 h-14 mx-auto bg-gradient-to-br ${iconGradient} rounded-xl flex items-center justify-center text-white text-xl mb-4 shadow-md`}>
                {iconMap[stat.icon] && <FontAwesomeIcon icon={iconMap[stat.icon]} />}
              </div>
              <div className="text-2xl md:text-3xl font-bold text-gray-800 mb-1">{stat.value}</div>
              <p className="text-gray-600 font-medium text-xs uppercase tracking-wider">
                {language === 'en' ? stat.labelEn : stat.labelId}
              </p>
              {stat.subtitle && (
                <p className="text-gray-600 text-[10px] mt-1 font-medium">({stat.subtitle})</p>
              )}
            </div>
          );
          })}
        </div>



        {/* Detailed Kepegawaian Charts Grid */}
        {kepegawaianStats && (
        <div className="grid md:grid-cols-2 gap-8 mt-8" data-aos="fade-up">

            {/* 1. Lecturer Ranks (Jabfung) */}
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 min-h-[400px] flex flex-col">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-unmulYellow/20 rounded-lg flex items-center justify-center">
                    <FontAwesomeIcon icon={faChartBar} className="text-unmulYellow text-sm" />
                </span>
                {language === 'en' ? 'Lecturer Ranks' : 'Jabatan Fungsional Dosen'}
                </h3>
                <div className="space-y-4 mt-6 flex-1">
                {dynamicStaffData.map((item, index) => (
                    <div key={item.name}>
                    <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-700">{item.name}</span>
                        <span className="text-sm font-bold text-gray-800">{item.value}</span>
                    </div>
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div
                        className="h-full rounded-full transition-all duration-1000 ease-out"
                        style={{
                            width: `${(item.value / 400) * 100}%`, // Approx scale
                            backgroundColor: item.color
                        }}
                        />
                    </div>
                    </div>
                ))}
                </div>
                <div className="mt-4 pt-4 border-t text-xs text-gray-500 text-center">
                    Total {dosenTotal} Dosen
                </div>
            </div>

            {/* 2. Dosen Education (S2/S3) */}
            {dosenEducationData.length > 0 && (
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 min-h-[400px] flex flex-col">
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <span className="w-8 h-8 bg-unmulGreen/10 rounded-lg flex items-center justify-center">
                            <FontAwesomeIcon icon={faUserGraduate} className="text-unmulGreen text-sm" />
                        </span>
                        {language === 'en' ? 'Lecturer Education' : 'Pendidikan Dosen'}
                    </h3>
                    <div className="flex-1 relative">
                        <Doughnut 
                            data={{
                                labels: dosenEducationData.map(d => d.name),
                                datasets: [{
                                    data: dosenEducationData.map(d => d.value),
                                    backgroundColor: dosenEducationData.map(d => d.color),
                                    borderWidth: 2,
                                    borderColor: '#ffffff'
                                }]
                            }}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: { legend: { position: 'right', labels: { boxWidth: 10, font: { size: 10 } } } }
                            }}
                        />
                    </div>
                </div>
            )}

            {/* 3. Dosen Serdos */}
            {dosenSerdosData.length > 0 && (
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 min-h-[400px] flex flex-col">
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
                            <FontAwesomeIcon icon={faIdBadge} className="text-blue-600 text-sm" />
                        </span>
                        {language === 'en' ? 'Lecturer Certification' : 'Sertifikasi Dosen'}
                    </h3>
                    <div className="flex-1 relative">
                        <Doughnut 
                            data={{
                                labels: dosenSerdosData.map(d => d.name),
                                datasets: [{
                                    data: dosenSerdosData.map(d => d.value),
                                    backgroundColor: dosenSerdosData.map(d => d.color),
                                    borderWidth: 2,
                                    borderColor: '#ffffff'
                                }]
                            }}
                             options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: { legend: { position: 'right', labels: { boxWidth: 10, font: { size: 10 } } } }
                            }}
                        />
                    </div>
                </div>
            )}



            {/* 4. Employment Status */}
            {employmentData.datasets.length > 0 && (
                <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 min-h-[400px] flex flex-col">
                    <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                        <span className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                            <FontAwesomeIcon icon={faIdBadge} className="text-orange-600 text-sm" />
                        </span>
                        {language === 'en' ? 'Employment Status' : 'Status Kepegawaian'}
                    </h3>
                    <div className="flex-1">
                         <Bar 
                            data={employmentData}
                            options={{
                                responsive: true,
                                maintainAspectRatio: false,
                                plugins: { legend: { position: 'bottom' } },
                                scales: { x: { stacked: false }, y: { stacked: false } }
                            }}
                         />
                    </div>
                </div>
            )}

        </div>
        )}

        {/* Fallback / Original Layout if No Kepegawaian Data */}
        {!kepegawaianStats && (
            <div className="bg-white rounded-2xl p-6 shadow-lg border border-gray-100 mt-8" data-aos="fade-left">
                <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
                <span className="w-8 h-8 bg-unmulYellow/20 rounded-lg flex items-center justify-center">
                    <FontAwesomeIcon icon={faChartBar} className="text-unmulYellow text-sm" />
                </span>
                {language === 'en' ? 'Lecturer Ranks' : 'Jabatan Fungsional Dosen'}
                </h3>
                <div className="space-y-4 mt-6">
                {staffData.map((item, index) => (
                    <div key={item.name} data-aos="fade-left" data-aos-delay={index * 100}>
                    <div className="flex justify-between items-center mb-1">
                        <span className="text-sm font-medium text-gray-700">{item.name}</span>
                        <span className="text-sm font-bold text-gray-800">{item.value}</span>
                    </div>
                    <div className="h-3 bg-gray-100 rounded-full overflow-hidden">
                        <div
                        className="h-full rounded-full transition-all duration-1000 ease-out"
                        style={{
                            width: `${(item.value / 400) * 100}%`,
                            backgroundColor: item.color
                        }}
                        />
                    </div>
                    </div>
                ))}
                </div>
                <div className="mt-6 p-4 bg-unmulPale rounded-xl">
                <p className="text-sm text-gray-600 flex items-start gap-2">
                    <FontAwesomeIcon icon={faInfoCircle} className="text-unmulGreen mt-0.5" />
                    <span>{language === 'en' ? 'Total 1,078 lecturers with masters, doctoral, and professor qualifications' : 'Total 1.078 dosen dengan kualifikasi S2, S3, dan Guru Besar'}</span>
                </p>
                </div>
            </div>
        )}


        {/* Student By Year Bar Chart */}
        <div className="mt-8 bg-white rounded-2xl p-6 shadow-lg border border-gray-100 overflow-hidden" data-aos="fade-up">
          <h3 className="text-lg font-bold text-gray-800 mb-4 flex items-center gap-2">
            <span className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center">
              <FontAwesomeIcon icon={faCalendarAlt} className="text-blue-600 text-sm" />
            </span>
            {language === 'en' ? 'Active Students by Year' : 'Mahasiswa Aktif per Angkatan'}
          </h3>
          <div className="h-80 w-full overflow-hidden">
            {isMounted && studentByYear.length > 0 && (
              <Bar data={barChartData} options={barChartOptions} />
            )}
          </div>
        </div>
      </div>
    </section>
    
    {/* Program Studi Section (Faculty only) */}
    {webType === '1' && prodiList && prodiList.data && prodiList.data.length > 0 && (
      <ProdiSection prodiData={prodiList} />
    )}
    </>
  );
}
